import createAuth0Client from '@auth0/auth0-spa-js'
import Auth0Client from '@auth0/auth0-spa-js/dist/typings/Auth0Client'
import { getAuth0Settings, getPlatform } from '../PlatformUtils'
import { interval, Subscription } from 'rxjs'
import { ClientConfig } from '../config/ClientConfig'
import { myPlayer } from '../MyPlayer'
import { LogManager } from '../LogManager'
import { FeatureToggleService } from '../services/FeatureToggleService'
import type { Logger } from '../../../../shared/logging/types'
import {
	hasValidLocation,
	getGeoLocationState,
	getSubdivision,
	extractStateFromSubdivision,
} from '../services/GeoLocationService'
import { JWT_CLAIMS } from '../../../../shared/auth/jwt-claims'
import {
	buildAuth0BaseOptions,
	applyViewerCountryRegion,
	applyAuth0Cookies,
	type ExtendedAuth0ClientOptions,
} from '../utils/auth-utils'

const auth0Settings = getAuth0Settings()

// Build base Auth0 options using shared utility
const options: ExtendedAuth0ClientOptions = buildAuth0BaseOptions({
	domain: auth0Settings.domain,
	clientId: auth0Settings.clientId,
	audience: auth0Settings.audience,
	platform: getPlatform(),
	redirectUri: location.origin,
})

// Apply viewer country/region if available
applyViewerCountryRegion(options)

interface AdditionalClientOptions {
	from?: string
	referral?: string
	suppressAutoLogin?: boolean
}

class AuthServiceClass {
	private auth0Client!: Auth0Client
	private registrationDate: Date = new Date()
	private pokId!: number
	private cubeiaId: number = -1
	private referralCode: string | undefined
	private logger: Logger
	private sessionKeeperSubscription: Subscription | null = null
	private readyPromise: Promise<void>
	private resolveReady!: () => void
	private rejectReady!: (err: unknown) => void
	private autoLoginSuppressed = false

	constructor(logger: Logger) {
		this.logger = logger
		this.readyPromise = new Promise<void>((resolve, reject) => {
			this.resolveReady = resolve
			this.rejectReady = reject
		})
		this.setCookies()
	}

	async prepare(additionalOptions: AdditionalClientOptions): Promise<void> {
		try {
			const client = await createAuth0Client({ ...options, ...additionalOptions })
			this.auth0Client = client
			this.resolveReady()
		} catch (err) {
			this.rejectReady(err)
			throw err
		}

		// Store referral code for later use during login redirect
		if (additionalOptions.referral) {
			this.referralCode = additionalOptions.referral
		}

		if (additionalOptions.suppressAutoLogin) {
			this.autoLoginSuppressed = true
		}

		if (!this.autoLoginSuppressed) {
			const a0SessionKeeperInterval = ClientConfig.MAX_CLIENT_INACTIVITY_TIME - 1 * 60 * 1000
			this.sessionKeeperSubscription = interval(a0SessionKeeperInterval).subscribe(() => {
				this.getAccessToken()
			})
		}
	}

	async loginWithRedirect(options?: { [key: string]: string | boolean | object }): Promise<void> {
		// Promise intentionally never resolves.
		return new Promise(() => {
			let loginOptions = options || {}

			const { pathname, search } = window.location;
			if (pathname !== "/" && pathname !== "/login") {
				// Save full original subpath
				let returnTo : string  = pathname + search
				loginOptions = {
					...loginOptions,
					appState: { returnTo }
				};
			}

			if (this.referralCode) {
				loginOptions = {
					...loginOptions,
					referral: this.referralCode
				}
			}

			this.auth0Client.loginWithRedirect(loginOptions)
		})
	}

	async isAuthenticated(): Promise<boolean> {
		const authenticated = await this.auth0Client.isAuthenticated()
		if (authenticated) {
			await this.hydrateIdentity()
		}
		return authenticated
	}

	async handleRedirectCallback(): Promise<void> {
		const result = await this.auth0Client.handleRedirectCallback()
		const returnTo = result.appState?.returnTo || '/'
		window.history.replaceState({}, '', returnTo)
		await this.hydrateIdentity()
	}

	private async hydrateIdentity(): Promise<void> {
		try {
			await this.getTokenSilently()
		} catch {
			// Token not available - identity will remain unset
		}
	}

	async getAccessToken(): Promise<string> {
		if (ClientConfig.OVERRIDE_JWT) {
			return Promise.resolve(myPlayer.fakeJwtToken)
		}
		await this.readyPromise
		return this.getTokenSilently()
	}

	async getTokenSilently(): Promise<string> {
		return this.auth0Client.getTokenSilently()
			.then((token) => {
				const claims = this.extractClaims(token)

				const registrationDateUtc = claims[JWT_CLAIMS.REGISTRATION_DATE]
				if (registrationDateUtc) this.registrationDate = new Date(registrationDateUtc)

				this.pokId = claims[JWT_CLAIMS.POK_ID]
				this.cubeiaId = parseInt(claims[JWT_CLAIMS.CUBEIA_ID]) || -1

				return token
			})
			.catch((err) => {
				if (err.error === 'login_required' && !this.autoLoginSuppressed) {
					try { window.rmp?.stopLocatingDevice?.() } catch { /* ensure redirect fires even if SDK teardown throws */ }
					this.loginWithRedirect()
				}
				this.logger.warn({ message: 'Failed to get token silently', error: err.error })
				throw err
			})
	}

	private extractClaims(token: string): Lookup<any> {
		const parts = token.split('.')
		try {
			const encodedClaims = parts[1]
			return JSON.parse(decodeBase64(encodedClaims))
		} catch (err) {
			this.logger.warn({ message: 'Unexpected error extracting claims from Auth0 access token %s', err })
			return {}
		}
	}

	async getAccessTokenClaims(): Promise<Lookup<any>> {
		return this.getAccessToken().then((token) => this.extractClaims(token))
	}

	getCurrentUserIdSynchronous(): number {
		return this.pokId
	}

	getCubeiaIdSynchronous(): number {
		return this.cubeiaId
	}

	getCurrentUserId(): Promise<string> {
		return this.getAccessTokenClaims().then((atc) => atc[JWT_CLAIMS.POK_ID])
	}

	getCubeaiId(): Promise<string> {
		return this.getAccessTokenClaims().then((atc) => atc[JWT_CLAIMS.CUBEIA_ID])
	}

	getScreenName(): Promise<string> {
		return this.getAccessTokenClaims().then((atc) => atc[JWT_CLAIMS.SCREEN_NAME])
	}

	getUserState(): Promise<string> {
		return this.getAccessTokenClaims().then((atc) => atc[JWT_CLAIMS.STATE])
	}

	/**
	 * Returns user email from Auth0 user profile (may call /userinfo).
	 * Returns undefined if unavailable or on error.
	 */
	async getUserEmail(): Promise<string | undefined> {
		await this.readyPromise
		try {
			const user = await this.auth0Client.getUser()
			return user?.email
		} catch (err) {
			this.logger.warn({
				message: 'getUserEmail failed, falling back to undefined',
				error: err as Error,
			})
			return undefined
		}
	}

	getUserGeoLocationState(): Promise<string> {
		if (hasValidLocation()) {
			return Promise.resolve(getGeoLocationState().toLowerCase())
		}
		return this.getAccessTokenClaims().then((atc) => {
			const raw = atc[JWT_CLAIMS.VIEWER_COUNTRY_REGION]
			if (raw == null || String(raw).trim() === '') {
				this.logger.warn({
					message: 'JWT viewer_country_region claim missing or empty',
					caller: 'getUserGeoLocationState',
					claim: 'viewer_country_region',
					pokId: atc[JWT_CLAIMS.POK_ID],
				})
				return ''
			}
			const state = extractStateFromSubdivision(String(raw))
			return state.toLowerCase()
		})
	}

	/** Returns the full subdivision (e.g. "US-AZ") for display. Falls back to JWT viewer_country_region when no geo result exists. */
	getUserGeoSubdivision(): Promise<string> {
		if (hasValidLocation()) {
			return Promise.resolve(getSubdivision())
		}
		return this.getAccessTokenClaims().then((claims) => {
			const regionClaim = claims[JWT_CLAIMS.VIEWER_COUNTRY_REGION]
			return regionClaim != null && String(regionClaim).trim() !== '' ? String(regionClaim).trim() : ''
		})
	}

	getUserRegistrationDate(): Date {
		return this.registrationDate
	}

	async logout(returnTo?: string) {
		this.autoLoginSuppressed = true
		this.sessionKeeperSubscription?.unsubscribe()
		this.sessionKeeperSubscription = null
		try { window.rmp?.stopLocatingDevice?.() } catch { /* ensure logout fires even if SDK teardown throws */ }
		await LogManager.flush()
		return this.auth0Client.logout({
			client_id: auth0Settings.clientId,
			returnTo: returnTo ?? window.origin,
		})
	}

	setCookies(): void {
		// Apply Auth0-relevant cookies using shared utility
		applyAuth0Cookies(options)

		// Add blocked markets configuration
		options['blocked_markets_at_registration'] = FeatureToggleService.blockMarketFeature.getMarketsBlockedAtRegistration().join(',')
		options['blocked_markets_at_login'] = FeatureToggleService.blockMarketFeature.getMarketsBlockedAtLogin().join(',')
	}
}

export function createAuthService(logger?: Logger): AuthServiceClass {
	return new AuthServiceClass(logger ?? LogManager)
}

export const AuthService = createAuthService()

// This function cleans out illegal characters from the original base64 encoded string
// So we can support users' screen names containing illegal characters (old users only)
function decodeBase64(value: string) {
	const cleanValue = value.replace(/-/g, '+').replace(/_/g, '/').replace(/\s/g, '')
	return atob(cleanValue)
}

export const fetchUserRegion = async (): Promise<string> => {
	if (hasValidLocation()) {
		return getSubdivision()
	}
	const claims = await AuthService.getAccessTokenClaims()
	const viewerCountry = claims[JWT_CLAIMS.VIEWER_COUNTRY]
	const viewerCountryRegion = claims[JWT_CLAIMS.VIEWER_COUNTRY_REGION]
	if (viewerCountry == null || String(viewerCountry).trim() === '') {
		LogManager.warn({
			message: 'JWT viewer_country claim missing or empty',
			caller: 'fetchUserRegion',
			claim: 'viewer_country',
			pokId: claims[JWT_CLAIMS.POK_ID],
		})
		return ''
	}
	if (viewerCountryRegion == null || String(viewerCountryRegion).trim() === '') {
		LogManager.warn({
			message: 'JWT viewer_country_region claim missing or empty',
			caller: 'fetchUserRegion',
			claim: 'viewer_country_region',
			pokId: claims[JWT_CLAIMS.POK_ID],
		})
		return ''
	}
	return `${viewerCountry}-${viewerCountryRegion}`
}

